import {useMemo, useRef, useState, useEffect, useContext} from 'react'

import {Week} from './views/Week'
import {Navigation} from '../components/nav/Navigation'
import Editor from './views/Editor'
import {Day} from './views/Day'
import {Table, Wrapper} from '../styles/styles'
import {useStore} from '../store'
import {Timeline} from './views/Timeline'
import {ChartSummary} from '../../../../_fds/partials/widgets'
import {BaseEmployeeModel} from '../core/_models'
import {KTSVG} from '../../../../_fds/helpers'
import {
  convertBaseEmployeesToResources,
  toTitleCase,
  withUnassignedShiftRows,
} from '../helpers/generals'
import {EmployeesWeek} from './views/EmployeesWeek'
import LoadingOverlay from './common/LoadingOverlay'
import {FdsSelect} from './common/FdsSelect'
import {setStatusSlot} from './common/statusSlot'

// The views' names as a manager says them — toTitleCase('employeesWeek') gave
// "Employeeweek".
const VIEW_LABELS: Record<string, string> = {
  week: 'Week View',
  timeline: 'Timeline View',
  employeesWeek: 'Employee Week View',
}
// import scheduleService from '../../services/scheduleService';

const SchedulerComponent = () => {
  const {
    loading,
    view,
    events,
    dialog,
    resources,
    resourceViewMode,
    handleState,
    availablePositions,
    employees,
    selectedPosition,
    selectedDate,
    searchString,
    templateMode,
  } = useStore()

  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null) // Specify the type of the state

  useEffect(() => {
    // console.log('ALOHAHOLAHAAA')
    const resizeObserver = new ResizeObserver((entries) => {
      const {width, height} = entries[0].contentRect
      setContainerWidth(width)
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    // Template mode: resources are the template's placeholder rows — don't
    // clobber them with employee-derived ones.
    if (templateMode) return
    // console.log('RENDERING RESOURCE FILTERING', selectedPosition, searchString)
    // filter by position
    const filteredByPositionEmployees = employees.filter((employee: BaseEmployeeModel) => {
      if (selectedPosition === 'all') {
        return true
      }
      return employee.position === selectedPosition
    })

    if (searchString === '') {
      // console.log('filteredByPositionEmployees', filteredByPositionEmployees)
      handleState(
        withUnassignedShiftRows(
          convertBaseEmployeesToResources(filteredByPositionEmployees) || [],
          events
        ),
        'resources'
      )
      return
    }
    const filteredEmployees = filteredByPositionEmployees.filter((employee: BaseEmployeeModel) => {
      const fullName = `${employee.first_name.toLowerCase()} ${employee.last_name.toLowerCase()}`
      return fullName.toLowerCase().includes(searchString.toLowerCase())
    })
    handleState(
      withUnassignedShiftRows(convertBaseEmployeesToResources(filteredEmployees) || [], events),
      'resources'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, selectedPosition, employees])

  const chartSummary = useMemo(() => {
    return <ChartSummary className='mb-2' />
  }, [events])

  // const [schedules, setSchedules] = useState<any[]>([]);

  const Views = useMemo(() => {
    // console.log('RENDERING VIEWS')
    // console.log(selectedPosition, searchString)
    switch (view) {
      //   case 'month':
      //     return <Month />
      case 'employeesWeek':
        return <EmployeesWeek />
      case 'week':
        return <Week />
      // case 'day':
      //   return <Day />
      case 'timeline':
        return <Timeline containerWidth={containerWidth} />
      default:
        return ''
    }
  }, [view, selectedDate, selectedPosition, containerWidth])

  const renderContent = () => {
    // console.log('RENDERING SCHEDULER COMPONENT')
    return (
      <Wrapper dialog={dialog ? 1 : 0} data-testid='rs-wrapper' className=''>
        {loading && <LoadingOverlay />}
        {/* <Navigation /> */}

        {view === 'timeline' && !templateMode && chartSummary}
        <div className='d-flex flex-wrap flex-stack mb-6'>
          <h3 className='fw-semibold my-2'>
            {templateMode ? 'Template' : 'Schedule'}
            <span className='fs-6 text-gray-400 fw-bold ms-1 '>
              {templateMode ? 'editor' : VIEW_LABELS[view] || `${toTitleCase(view)} View`}
            </span>
          </h3>

          {/* Search + position filter act on employee resources — meaningless
              for template rows, so they hide in template mode. */}
          {!templateMode && (
            <div className='d-flex my-2'>
              <div className='d-flex align-items-center position-relative me-4'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-3 position-absolute ms-3'
                />
                <input
                  type='text'
                  id='fds_filter_search'
                  className='form-control form-control-white form-control-sm w-150px ps-9'
                  value={searchString}
                  onChange={(e) => {
                    handleState(e.target.value, 'searchString')
                  }}
                  placeholder='Search member'
                />
              </div>

              <FdsSelect
                ariaLabel='Position'
                value={selectedPosition}
                onChange={(position) => handleState(position, 'selectedPosition')}
                options={[
                  {value: 'all', label: 'All'},
                  ...availablePositions
                    .filter((pos) => pos.position && pos.position !== 'all') // 'all' is the store's placeholder
                    .map((pos) => ({
                      value: pos.position as string,
                      label: pos.name || toTitleCase(pos.position),
                    })),
                ]}
              />
            </div>
          )}
        </div>
        {/* The schedule's status, its changes and history (SchedulerDropdown
            portals them here). Not in template mode: the draft is not the
            schedule. */}
        {!templateMode && <div ref={setStatusSlot} className='mb-6' />}
        <Table
          ref={containerRef}
          resource_count={resourceViewMode === 'tabs' ? 1 : resources.length}
          // Temp resources/default `sticky` wontfix
          sx={{
            overflowX: resourceViewMode === 'default' && resources.length > 1 ? 'auto' : undefined,
          }}
          data-testid='grid'
        >
          {Views}
        </Table>
        {/* {dialog && <Editor />} */}
      </Wrapper>
    )
  }
  return renderContent()
}

export {SchedulerComponent}
